import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { en, type Dictionary } from './en';

/**
 * A deliberately small i18n layer.
 *
 * It exists from the first commit so no user-facing string is ever hardcoded in
 * a component. Only English ships; Greek is a second dictionary later, not a
 * refactor of every screen.
 */

type Primitive = string | number;

/** Dotted paths into the dictionary, checked at compile time. */
type Leaves<T, Prefix extends string = ''> = {
  [K in keyof T & string]: T[K] extends string
    ? `${Prefix}${K}`
    : Leaves<T[K], `${Prefix}${K}.`>;
}[keyof T & string];

export type MessageKey = Leaves<Dictionary>;

function lookup(dictionary: unknown, key: string): string | undefined {
  const value = key
    .split('.')
    .reduce<unknown>((node, part) => (node as Record<string, unknown> | undefined)?.[part], dictionary);
  return typeof value === 'string' ? value : undefined;
}

/**
 * `{name}` placeholders, plus a `_one` suffix for the singular when a `count`
 * is supplied. English needs no more plural machinery than that.
 */
export function translate(
  dictionary: Dictionary,
  key: string,
  params?: Record<string, Primitive>,
): string {
  const singular = params?.count === 1 ? lookup(dictionary, `${key}_one`) : undefined;
  const template = singular ?? lookup(dictionary, key);

  if (template === undefined) {
    // Loud in development, harmless in production: rendering the key beats
    // rendering an empty space where a label should be.
    if (import.meta.env.DEV) console.warn(`[i18n] missing message: ${key}`);
    return key;
  }
  if (!params) return template;

  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? String(params[name]) : match,
  );
}

export type TranslateFn = (key: MessageKey, params?: Record<string, Primitive>) => string;

const fallback: TranslateFn = (key, params) => translate(en, key, params);

const I18nContext = createContext<{ t: TranslateFn; locale: string }>({
  t: fallback,
  locale: 'en',
});

export function I18nProvider({ children }: { children: ReactNode }) {
  const value = useMemo(() => ({ t: fallback, locale: 'en' }), []);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useT(): TranslateFn {
  return useContext(I18nContext).t;
}

export function useLocale(): string {
  return useContext(I18nContext).locale;
}
